import {
    AUTOTRANSLATOR_TRANSLATE_MODEL,
    calculateOpenAITextCostUsd,
    type AutotranslatorTextBilling,
    type OpenAITextTokenUsage
} from "./autotranslatorCost";

// A fallback only: provider usage always wins. Korean syllables usually need
// more tokens than Latin characters; JSON/schema overhead is included separately.
export const estimateAutotranslatorTextTokens = (text: string): number => {
    let units = 0;
    for (const character of text) units += character.charCodeAt(0) > 127 ? 1.5 : 0.25;
    return Math.ceil(units);
};
export const estimateAutotranslatorInputTokens = (prompt: string): number =>
    estimateAutotranslatorTextTokens(prompt) + 192;

export type AutotranslatorBillingProgress = {
    attempt: number;
    inputTokenEstimate: number;
    outputTokenEstimate: number | null;
};

type Estimate = {
    input: number;
    output: number | null;
    attempts: number;
    missing: number;
    knownCost: number;
};
type Sample = { inputScale: number; outputTokens: number; outputScale: number; cached: number; written: number };
const finite = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value) && value >= 0;
const clamp = (value: number, minimum: number, maximum: number) => Math.min(maximum, Math.max(minimum, value));

// One instance belongs to one listening run. Records contain numeric sizes,
// never conversation text. Later receipts refine interrupted requests locally.
export class AutotranslatorTextMeter {
    private nextId = 0;
    private pending = new Map<number, Estimate>();
    private samples: Sample[] = [];
    private settledCost = 0;
    private settledRequests = 0;

    begin(input: number): number {
        const id = ++this.nextId;
        this.pending.set(id, { input, output: null, attempts: 1, missing: 1, knownCost: 0 });
        return id;
    }

    progress(id: number, progress: AutotranslatorBillingProgress): void {
        const record = this.pending.get(id);
        if (!record || !finite(progress.attempt) || progress.attempt < 1 ||
            !finite(progress.inputTokenEstimate) ||
            !(progress.outputTokenEstimate === null || finite(progress.outputTokenEstimate))) return;
        const attempt = Math.floor(progress.attempt);
        if (attempt < record.attempts) return;
        if (attempt > record.attempts) record.output = null;
        record.attempts = attempt;
        record.missing = attempt;
        record.input = progress.inputTokenEstimate;
        record.output = progress.outputTokenEstimate;
    }

    settle(id: number, billing: AutotranslatorTextBilling): void {
        const record = this.pending.get(id);
        if (!record || !finite(billing.textCostUsd) || !finite(billing.requestCount)) return;
        record.attempts = Math.max(record.attempts, Math.floor(billing.requestCount));
        record.knownCost = billing.textCostUsd;
        record.missing = billing.usageComplete ? 0 : finite(billing.missingUsageRequestCount)
            ? clamp(Math.floor(billing.missingUsageRequestCount), 1, record.attempts) : record.attempts;
        if (record.missing) return;

        this.settledCost += record.knownCost;
        this.settledRequests += record.attempts;
        this.pending.delete(id); // A duplicate or late progress event cannot charge twice.
        const usage = billing.usage;
        if (billing.model !== AUTOTRANSLATOR_TRANSLATE_MODEL || billing.requestCount !== 1 ||
            !usage || !finite(usage.inputTokens) || !finite(usage.outputTokens) ||
            !finite(usage.cachedInputTokens) || !finite(usage.cacheWriteTokens) ||
            usage.inputTokens <= 0 || record.input <= 0) return;
        this.samples.push({
            inputScale: clamp(usage.inputTokens / record.input, 0.25, 4),
            outputTokens: Math.min(900, usage.outputTokens),
            outputScale: record.output ? clamp(usage.outputTokens / record.output, 0.25, 8) : 1.5,
            cached: clamp(usage.cachedInputTokens / usage.inputTokens, 0, 1),
            written: clamp(usage.cacheWriteTokens / usage.inputTokens, 0, 1)
        });
        this.samples = this.samples.slice(-16);
    }

    skip(id: number): void {
        if (!this.pending.delete(id)) return;
        // No model dispatch, so no token charge or request count.
    }

    private average(key: keyof Sample, fallback: number): number {
        return this.samples.length ? this.samples.reduce((sum, sample) => sum + sample[key], 0) / this.samples.length : fallback;
    }

    get estimatedRequests(): number {
        return [...this.pending.values()].reduce((sum, record) => sum + record.missing, 0);
    }

    get requestCount(): number {
        return this.settledRequests + [...this.pending.values()].reduce((sum, record) => sum + record.attempts, 0);
    }

    get costUsd(): number {
        let cost = this.settledCost;
        const inputScale = this.average("inputScale", 1);
        const cached = this.average("cached", 0);
        const written = this.average("written", 1);
        const defaultOutput = this.average("outputTokens", 192);
        const outputScale = this.average("outputScale", 1.5);
        for (const record of this.pending.values()) {
            const inputTokens = Math.ceil(record.input * inputScale);
            const cachedInputTokens = Math.floor(inputTokens * cached);
            const usage: OpenAITextTokenUsage = {
                inputTokens,
                cachedInputTokens,
                cacheWriteTokens: Math.min(inputTokens - cachedInputTokens,
                    Math.floor(inputTokens * written)),
                outputTokens: record.output === null ? defaultOutput
                    : Math.min(900, Math.max(32, record.output * outputScale))
            };
            cost += record.knownCost + record.missing * (calculateOpenAITextCostUsd(AUTOTRANSLATOR_TRANSLATE_MODEL, usage) ?? 0);
        }
        return cost;
    }
}
